import { useEffect, useState, useMemo } from 'react';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import Tiptap from '@Components/TipTap';
import { useInboxes } from '@Hooks/useInboxes';
import { useMessages } from '@Contexts/messages';
import { useAuth } from '@Contexts/auth';
import { filterInboxesByPermissions } from '@Components/Inbox/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import service from '@Service';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useQueryClient } from '@tanstack/react-query';

export default function Email({
  sendToId,
  sendToEmail,
  sendToName,
  closeModal,
}) {
  const [fromInboxId, setFromInboxId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [sending, setSending] = useState(false);

  const client = useQueryClient();

  const { data: allInboxes, status } = useInboxes({
    searchParams: {
      'filter[inboxType]': 'EMAIL',
      'include': 'user',
    },
  });
  const { user } = useAuth();
  const { userPermissions, fetchUserPermissions } = useMessages();

  // Restrict senders to the inboxes this user may send through — the same
  // permission model the /inbox view uses.
  const inboxes = useMemo(
    () =>
      userPermissions
        ? filterInboxesByPermissions(allInboxes ?? [], userPermissions, user?.id)
        : [],
    [allInboxes, userPermissions, user?.id],
  );

  useEffect(() => {
    if (user?.id) fetchUserPermissions(user.id);
  }, [user?.id]);

  useEffect(() => {
    if (inboxes.length === 0) return;
    // Keep the current pick only while it stays permitted.
    if (!inboxes.some((inbox) => inbox.id === fromInboxId)) {
      setFromInboxId(inboxes[0].id);
    }
  }, [inboxes, fromInboxId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!fromInboxId || !subject.trim() || !body.trim()) return;

    setSending(true);

    try {
      // Single recipient messaging
      const result = await service.sendMessage({
        fromInboxId,
        toFishId: sendToId,
        method: 'EMAIL',
        messageBody: body?.trim(),
        messageSubject: subject?.trim(),
      });

      if (!result.success) {
        throw new Error(result?.error || 'Failed to send email.');
      }

      setSubject('');
      setBody('');

      toastSuccess(`Email sent successfully.`);
      client.invalidateQueries({ queryKey: ['emailThreads'] });
      closeModal();
    } catch (err) {
      toastError(err.message || `Failed to send email.`);
    } finally {
      setSending(false);
    }
  };

  const canSend =
    fromInboxId &&
    subject.trim() &&
    body.replace(/<[^>]*>/g, '').trim() &&
    !sending;

  return (
    <div className="mt-3 rounded-lg bg-white text-black">
      <form onSubmit={handleSend} className="space-y-4">
        {/* To (read-only, provided by the parent) */}
        <div className="flex gap-4">
          <div className="w-1/2">
            <label className="block text-xs font-medium text-gray-500">
              To
            </label>
            <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {sendToName} / {sendToEmail}
            </div>
          </div>

          {/* From selector */}
          <div className="w-1/2">
            <label
              htmlFor="fromInbox"
              className="block text-xs font-medium text-gray-700"
            >
              From
            </label>
            {status !== 'success' ? (
              <div className="mt-1 text-sm text-gray-700">Loading inboxes…</div>
            ) : (
              <div className="mt-1 w-full text-gray-700">
                <Select value={fromInboxId} onValueChange={setFromInboxId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an inbox" />
                  </SelectTrigger>
                  <SelectContent>
                    {inboxes?.length === 0 && (
                      <SelectItem value="n/a">No inboxes available</SelectItem>
                    )}
                    {inboxes?.map((inbox) => (
                      <SelectItem key={inbox.id} value={inbox.id}>
                        {`${inbox?.user?.name} / ${inbox.email}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Subject */}
        <div>
          <label
            htmlFor="subject"
            className="block text-xs font-medium text-gray-700"
          >
            Subject
          </label>
          <input
            id="subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none"
            placeholder="Subject"
          />
        </div>

        {/* Body */}
        <div>
          <label
            htmlFor="body"
            className="block text-xs font-medium text-gray-700"
          >
            Message
          </label>
          <Tiptap
            className="text-black"
            value={body}
            onChange={setBody}
            onBlur={() => {}}
            placeholder="Message body..."
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={!canSend}>
            {sending ? 'Sending…' : 'Send email'}
          </Button>
        </div>
      </form>
    </div>
  );
}
